import { getCurrentEnv, ENDPOINTS } from '../API';
import { getLang } from '../i18n';

export const redirectToPilot = (path, endpoint) => {
  let redirectTo = window.location.href.replace(window.location.host, endpoint || ENDPOINTS.pilot.app);
  if (path) {
    redirectTo = `https://${endpoint || ENDPOINTS.pilot.app}${path}`;
  }
  if (redirectTo) {
    window.location.href = redirectTo;
  }
};

export const redirectToPrebookPreview = (aPrebook) => {
  const environmentPrefix = getCurrentEnv() === 'production' ? 'admin' : `admin-${getCurrentEnv()}`;
  window.open(`https://${environmentPrefix}.tulka.com/admin/prebooking/${aPrebook.id}`, '_blank').focus();
};

export const getViewState = (error) => {
  let stateName = null;
  if (typeof error === 'string') {
    if (error.includes('Contact info not unique')) {
      if (error.includes('Contact info not unique type: Interpreter')) {
        stateName = 'notUniquePhone';
      } else { 
        stateName = 'notUnicEmail';
      }
    }

    if (error.includes('Invalid domain')) {
      stateName = 'invalidDomain';
    }
  }
  return stateName;
};

// Self-registration is served from one set of paths per UI language, all rendered by
// the same components via react-router's array-of-paths. Slugs are ASCII-only so the
// URLs survive being pasted into emails and SMS, and the Finnish ones are permanent
// aliases — verification links already sent out must keep working.
const SELFREGISTRATION_SLUGS = {
  fi: {
    base: 'rekisteroidy',
    check_email: 'vahvista-sahkoposti',
    phone: 'puhelinnumero',
    check_phone: 'vahvista-puhelinnumero',
    cost_center: 'kustannuspaikka',
    password: 'salasana',
    verification: 'rekisterointi-valmis',
  },
  en: {
    base: 'register',
    check_email: 'confirm-email',
    phone: 'phone-number',
    check_phone: 'confirm-phone',
    cost_center: 'cost-center',
    password: 'password',
    verification: 'registration-complete',
  },
  no: {
    base: 'registrer-deg',
    check_email: 'bekreft-epost',
    phone: 'telefonnummer',
    check_phone: 'bekreft-telefon',
    cost_center: 'kostnadssted',
    password: 'passord',
    verification: 'registrering-fullfort',
  },
  sv: {
    base: 'registrera',
    check_email: 'bekrafta-epost',
    phone: 'telefonnummer',
    check_phone: 'bekrafta-telefon',
    cost_center: 'kostnadsstalle',
    password: 'losenord',
    verification: 'registrering-klar',
  },
  da: {
    base: 'opret-konto',
    check_email: 'bekraeft-email',
    phone: 'telefonnummer',
    check_phone: 'bekraeft-telefon',
    cost_center: 'omkostningssted',
    password: 'adgangskode',
    verification: 'registrering-faerdig',
  },
  de: {
    base: 'registrieren',
    check_email: 'email-bestaetigen',
    phone: 'telefonnummer',
    check_phone: 'telefon-bestaetigen',
    cost_center: 'kostenstelle',
    password: 'passwort',
    verification: 'registrierung-abgeschlossen',
  },
  fr: {
    base: 'inscription',
    check_email: 'confirmer-email',
    phone: 'numero-de-telephone',
    check_phone: 'confirmer-telephone',
    cost_center: 'centre-de-couts',
    password: 'mot-de-passe',
    verification: 'inscription-terminee',
  },
};

// auth-complete is only ever an internal history.replace() target — never shown to a
// user and never emailed — so its segment stays untranslated.
const AUTH_COMPLETE_SLUG = 'auth-complete';

// Steps that live under the language's base path.
const STEP_KEYS = ['check_email', 'phone', 'check_phone', 'cost_center', 'password'];

export const SELFREGISTRATION_ROUTE_KEYS = [
  'email',
  ...STEP_KEYS,
  'auth_complete',
  'verification',
];

// Falls back to the Finnish paths, which is what every URL was before localization.
const FALLBACK_ROUTE_LANG = 'fi';

const buildRoutes = (slugs) => {
  const routes = {
    email: `/${slugs.base}`,
    auth_complete: `/${slugs.base}/${AUTH_COMPLETE_SLUG}`,
    verification: `/${slugs.verification}`,
  };
  STEP_KEYS.forEach(key => {
    routes[key] = `/${slugs.base}/${slugs[key]}`;
  });
  return routes;
};

const ROUTES_BY_LANG = Object.keys(SELFREGISTRATION_SLUGS).reduce((acc, lang) => {
  acc[lang] = buildRoutes(SELFREGISTRATION_SLUGS[lang]);
  return acc;
}, {});

/**
 * Every path a given self-registration step answers to, for `<Route path={[...]}>`.
 * Registering all languages on every step means switching language mid-wizard can
 * never land on an unrouted URL.
 */
export const SELFREGISTRATION_ROUTE_ALIASES = SELFREGISTRATION_ROUTE_KEYS.reduce((acc, key) => {
  acc[key] = Object.keys(ROUTES_BY_LANG).map(lang => ROUTES_BY_LANG[lang][key]);
  return acc;
}, {});

/**
 * The self-registration paths for one language.
 * @param {string} [lang] language code; defaults to the active UI language
 * @returns {object} `{ email, check_email, phone, check_phone, cost_center, password, auth_complete, verification }`
 */
export const getSelfRegistrationRoutes = (lang) => {
  return ROUTES_BY_LANG[lang || getLang()] || ROUTES_BY_LANG[FALLBACK_ROUTE_LANG];
};

const stripTrailingSlash = (pathname) => {
  const path = pathname || '';
  return path.length > 1 ? path.replace(/\/+$/, '') : path;
};

/**
 * Which language's self-registration paths a pathname belongs to.
 * @param {string} pathname
 * @returns {string|null} language code, or null when the path is not part of the flow
 */
export const getLangFromSelfRegistrationPath = (pathname) => {
  const path = stripTrailingSlash(pathname);
  const match = Object.keys(ROUTES_BY_LANG).find(lang => {
    const routes = ROUTES_BY_LANG[lang];
    return path === routes.email || path.startsWith(`${routes.email}/`) || path === routes.verification;
  });
  return match || null;
};

/**
 * Whether a pathname is any page of the self-registration flow.
 * @param {string} pathname
 * @returns {boolean}
 */
export const isSelfRegistrationPath = (pathname) => {
  return getLangFromSelfRegistrationPath(pathname) !== null;
};

/**
 * Whether a pathname is the entry page of the flow, in any language.
 * @param {string} pathname
 * @returns {boolean}
 */
export const isSelfRegistrationEntryPath = (pathname) => {
  return SELFREGISTRATION_ROUTE_ALIASES.email.includes(stripTrailingSlash(pathname));
};

/**
 * The equivalent self-registration path in another language, so the URL can follow
 * the language switcher.
 * @param {string} pathname
 * @param {string} targetLang
 * @returns {string|null} the translated path, or null when there is no equivalent
 */
export const translateSelfRegistrationPath = (pathname, targetLang) => {
  const path = stripTrailingSlash(pathname);
  const sourceLang = getLangFromSelfRegistrationPath(path);
  const targetRoutes = ROUTES_BY_LANG[targetLang];
  if (!sourceLang || !targetRoutes) {
    return null;
  }
  const sourceRoutes = ROUTES_BY_LANG[sourceLang];
  const routeKey = SELFREGISTRATION_ROUTE_KEYS.find(key => sourceRoutes[key] === path);
  return routeKey ? targetRoutes[routeKey] : null;
};

export const REDIRECT_DELAY = 1000;